import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Dialog, DialogTitle, DialogBody, DialogActions } from './dialog';
import { Button } from './button';
import { Input } from './input';
import { Field, Label } from './fieldset';
import { Text } from './text';
import { Heading } from './heading';
import {
  CheckIcon,
  ClockIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';

const InviteClientUserDialog = ({ isOpen, onClose, onCreated, clientsList, preSelectedClientId }) => {
  const [nombre, setNombre] = useState('');
  const [correo, setCorreo] = useState('');
  const [selectedClientIds, setSelectedClientIds] = useState(
    preSelectedClientId ? [preSelectedClientId] : []
  );
  const [notificar, setNotificar] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [copyTooltip, setCopyTooltip] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const handleClose = () => {
    if (isCreating) return;
    setNombre('');
    setCorreo('');
    setSelectedClientIds(preSelectedClientId ? [preSelectedClientId] : []);
    setNotificar(true);
    setError('');
    setResult(null);
    setSearchTerm('');
    onClose();
  };

  const toggleClientId = (clientId) => {
    setSelectedClientIds((prev) =>
      prev.includes(clientId)
        ? prev.filter((id) => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleCreate = async () => {
    if (!nombre.trim() || !correo.trim()) {
      setError('Por favor completa nombre y correo.');
      return;
    }
    if (selectedClientIds.length === 0) {
      setError('Selecciona al menos un cliente.');
      return;
    }

    setIsCreating(true);
    setError('');

    try {
      const functions = getFunctions();
      const createClientUser = httpsCallable(functions, 'createClientUser');
      const res = await createClientUser({
        nombre: nombre.trim(),
        correo: correo.trim(),
        clientIds: selectedClientIds,
        notificarPorCorreo: notificar,
      });
      setResult(res.data);
      if (onCreated) onCreated({ nombre: nombre.trim(), correo: correo.trim(), clientIds: selectedClientIds });
    } catch (err) {
      console.error('Error creating client user:', err);
      let msg = 'Error al crear el usuario cliente.';
      if (err.code === 'functions/already-exists' || err.message === 'email-already-in-use') {
        msg = 'Este correo ya tiene una cuenta. Cada correo puede tener una sola cuenta; usa otro correo.';
      } else if (err.code === 'functions/invalid-argument' || err.message === 'invalid-email') {
        msg = 'Correo electrónico inválido.';
      } else if (err.code === 'functions/permission-denied') {
        msg = 'No tienes permisos para crear usuarios cliente.';
      }
      setError(msg);
    } finally {
      setIsCreating(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyTooltip('Copiado!');
      setTimeout(() => setCopyTooltip(''), 2000);
    } catch (err) {
      setCopyTooltip('Error');
      setTimeout(() => setCopyTooltip(''), 2000);
    }
  };

  const filteredClients = (clientsList || [])
    .filter((c) => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (
        (c.nombreEmpresa || '').toLowerCase().includes(term) ||
        (c.rfc || '').toLowerCase().includes(term)
      );
    })
    .sort((a, b) => {
      // Pre-selected client always on top
      const aSelected = selectedClientIds.includes(a.id) ? 0 : 1;
      const bSelected = selectedClientIds.includes(b.id) ? 0 : 1;
      if (aSelected !== bSelected) return aSelected - bSelected;
      return (a.nombreEmpresa || '').localeCompare(b.nombreEmpresa || '');
    });

  return (
    <Dialog open={isOpen} onClose={handleClose} size="lg">
      <DialogTitle>Invitar Usuario Cliente</DialogTitle>
      <DialogBody>
        {result ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-green-700">
              <CheckIcon className="w-5 h-5" />
              <Heading level={3}>Usuario cliente creado</Heading>
            </div>

            <div className="space-y-2">
              <div>
                <Text className="text-sm font-medium text-gray-500">Nombre</Text>
                <Text>{result.userName}</Text>
              </div>
              <div>
                <Text className="text-sm font-medium text-gray-500">Correo</Text>
                <Text>{result.userEmail}</Text>
              </div>
            </div>

            {result.emailSent && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <Text className="text-sm text-green-800">
                  Se envió un correo de bienvenida a {result.userEmail} con el
                  enlace para establecer su contraseña.
                </Text>
              </div>
            )}

            {result.resetLink && (
              <div>
                <Text className="text-sm font-medium text-gray-500 mb-1">
                  Enlace para establecer contraseña:
                </Text>
                <div className="bg-gray-50 p-3 rounded-lg border break-all text-sm font-mono flex items-center justify-between gap-2">
                  <span className="flex-1">{result.resetLink}</span>
                  <div className="relative">
                    <button
                      onClick={() => copyToClipboard(result.resetLink)}
                      className="p-2 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
                    >
                      <ClipboardDocumentIcon className="w-4 h-4 text-gray-600" />
                    </button>
                    {copyTooltip && (
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                        {copyTooltip}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-2 flex items-start gap-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <ClockIcon className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <Text className="text-sm text-yellow-800">
                    <strong>Importante:</strong> Este enlace expira en 1 hora.
                  </Text>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field>
                <Label>Nombre completo *</Label>
                <Input
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  placeholder="Nombre del usuario"
                  disabled={isCreating}
                />
              </Field>
              <Field>
                <Label>Correo electrónico *</Label>
                <Input
                  type="email"
                  value={correo}
                  onChange={(e) => setCorreo(e.target.value)}
                  placeholder="usuario@empresa.com"
                  disabled={isCreating}
                />
              </Field>
            </div>

            <div>
              <Text className="text-sm font-medium mb-2 block">Clientes asignados *</Text>
              <Input
                placeholder="Buscar cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="mb-2"
                disabled={isCreating}
              />
              <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                {filteredClients.length === 0 ? (
                  <div className="p-3 text-center">
                    <Text className="text-sm text-gray-400">No se encontraron clientes.</Text>
                  </div>
                ) : (
                  filteredClients.map((client) => (
                    <label
                      key={client.id}
                      className={`flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                        selectedClientIds.includes(client.id) ? 'bg-azul-surelia/5' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedClientIds.includes(client.id)}
                        onChange={() => toggleClientId(client.id)}
                        disabled={isCreating}
                        className="rounded border-gray-300 text-azul-surelia focus:ring-azul-surelia"
                      />
                      <div className="min-w-0 flex-1">
                        <Text className="text-sm font-medium truncate">
                          {client.nombreEmpresa || 'Sin nombre'}
                        </Text>
                        {client.rfc && (
                          <Text className="text-xs text-gray-400 font-mono">{client.rfc}</Text>
                        )}
                      </div>
                    </label>
                  ))
                )}
              </div>
              {selectedClientIds.length > 0 && (
                <Text className="text-xs text-gray-500 mt-1">
                  {selectedClientIds.length} cliente{selectedClientIds.length > 1 ? 's' : ''} seleccionado{selectedClientIds.length > 1 ? 's' : ''}
                </Text>
              )}
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={notificar}
                onChange={() => setNotificar(!notificar)}
                disabled={isCreating}
                className="rounded border-gray-300 text-azul-surelia focus:ring-azul-surelia"
              />
              <Text className="text-sm font-medium">
                Enviar correo de bienvenida al nuevo usuario
              </Text>
            </label>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <Text className="text-sm text-blue-800">
                <strong>Nota:</strong> El usuario cliente tendrá acceso de solo lectura a los
                expedientes, contratos y fianzas de los clientes seleccionados.
              </Text>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <Text className="text-sm text-red-700">{error}</Text>
              </div>
            )}
          </div>
        )}
      </DialogBody>
      <DialogActions>
        {result ? (
          <Button onClick={handleClose}>Cerrar</Button>
        ) : (
          <>
            <Button variant="outline" onClick={handleClose} disabled={isCreating}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreate}
              disabled={isCreating || !nombre.trim() || !correo.trim() || selectedClientIds.length === 0}
            >
              {isCreating ? 'Creando...' : 'Crear Usuario Cliente'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InviteClientUserDialog;
